import { AppRoutes } from "@/shared/lib/routes"
import { WebappRoutes } from "@/shared/lib/webapp-routes"
import { createRoute } from "@tanstack/react-router"
import { useNavigateWithParams } from "@/shared/hooks/use-navigate-with-params"
import { useEffect, useMemo } from "react"
import { rootRoute } from "./__root"
import {
  OfferStepper,
  resolveOfferEntries,
  OFFER_PRESETS,
  useOffersFunnelStore,
} from "@/features/offers"
import { usePurchaseStore } from "@/stores/purchase-store"
import { useSubscriptionStore } from "@/stores/subscription-store"
import { paymentService } from "@/features/payments"
import { syncUserProfileFromQuiz } from "@/features/user"
import { requestIncludedReportsForMe } from "@/features/today"
import { auth } from "@/features/auth/providers/firebase"
import { useQuizStore } from "@/stores/quiz-store"
import { ANALYTICS_EVENTS, trackEvent } from "@/features/analytics"
import { MobileLayout } from "@/shared/components/mobile-layout"
import { getOS } from "@/shared/lib/native-browser"
import { AppleIcon, GoogleIcon, SignInHero, SignInLoader, useSignIn } from "@/features/auth"

export const createAccountRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: AppRoutes.CREATE_ACCOUNT,
  component: CreateAccountPage,
})

/**
 * Post-purchase sign-in: the final step after checkout/upsell. Creating the
 * account binds the just-completed purchase to it (finalizes the subscription)
 * and seeds the backend profile from the quiz. For a plain returning-user login,
 * see `/signin` (the main sign-in screen).
 */
function CreateAccountPage() {
  const navigate = useNavigateWithParams()
  const { status: authStatus, user, handleApple, handleGoogle } = useSignIn()
  const purchaseStatus = usePurchaseStore((s) => s.status)
  const purchaseSubId = usePurchaseStore((s) => s.paymentIntentId)
  const purchasePlanId = usePurchaseStore((s) => s.planId)
  const email = (useQuizStore((s) => s.answers.email) as string) || ""
  const grantLocalAccess = useSubscriptionStore((s) => s.grantLocalAccess)

  // Funnel progress shown above the account step: all offers done, Register active.
  const offerLabels = useMemo(
    () => resolveOfferEntries().map((e) => OFFER_PRESETS[e.key]?.stepLabel ?? ""),
    [],
  )

  // The funnel is over — reset its position so a future purchase starts fresh.
  useEffect(() => {
    useOffersFunnelStore.getState().reset()
  }, [])

  useEffect(() => {
    trackEvent(ANALYTICS_EVENTS.SIGN_IN_VIEWED)
  }, [])

  useEffect(() => {
    if (authStatus !== "authenticated" || !user) return

    let cancelled = false

    ;(async () => {
      if (purchaseStatus === "success") {
        // Bind the no-user purchase to this account so it verifies by uid.
        if (purchaseSubId && user.uid !== "dev-user") {
          try {
            await paymentService.finalizeSubscription({
              subscriptionId: purchaseSubId,
              email: email || user.email || "",
              // Apple sign-in is identified by the Apple token; others by firebaseId.
              ...(user.appleToken ? { appleToken: user.appleToken } : { firebaseId: user.uid }),
              plan: purchasePlanId ?? undefined,
            })
          } catch (err) {
            console.error("finalize failed", err)
          }
        }
        // Demo/no-backend fallback (ignored once a backend verifies access).
        grantLocalAccess()
      }

      // Create/refresh the backend profile from the quiz data (year, zodiac, …)
      // so the Today tab can later read it server-side. Non-blocking: never gate
      // entering the app on this.
      try {
        await syncUserProfileFromQuiz()
      } catch (err) {
        console.error("user profile sync failed", err)
      }

      // Main plans that include Soulmate / Astro Map: create the report job for
      // the signed-in account (single path — no public checkout request).
      if (purchaseStatus === "success" && purchaseSubId && purchasePlanId) {
        const firebaseUser = auth.currentUser
        if (firebaseUser) {
          try {
            const token = await firebaseUser.getIdToken()
            await requestIncludedReportsForMe({
              planId: purchasePlanId,
              subscriptionId: purchaseSubId,
              token,
            })
          } catch (err) {
            console.error("included report request failed:", err)
          }
        }
      }

      if (cancelled) return
      trackEvent(ANALYTICS_EVENTS.SIGN_UP, { userId: user.uid })
      navigate({ to: WebappRoutes.ADVISORS })
    })()

    return () => {
      cancelled = true
    }
  }, [
    authStatus,
    user,
    navigate,
    purchaseStatus,
    purchaseSubId,
    purchasePlanId,
    email,
    grantLocalAccess,
  ])

  // While Firebase resolves the session (incl. returning from a redirect),
  // show a loader instead of flashing the sign-in buttons.
  if (authStatus === "loading" || authStatus === "authenticated") {
    return <SignInLoader />
  }

  // Apple ID is virtually unused on Android — hide that button there (Google only).
  const isAndroid = getOS() === "android"

  return (
    <div className="flex min-h-[100dvh] flex-col bg-dark-base font-body text-purple-light">
      {offerLabels.length > 0 && (
        <div className="mx-auto w-full max-w-app pt-2">
          <OfferStepper
            offerLabels={offerLabels}
            currentIndex={offerLabels.length}
            className="border-b border-purple-light/10"
          />
        </div>
      )}

      <MobileLayout className="flex flex-1 flex-col items-center justify-center gap-6 py-12">
        <SignInHero />

        <div className="space-y-2 text-center">
          <h1 className="font-display text-2xl font-medium leading-tight text-purple-light">
            Your Reports and Personal Insights Are Ready!
          </h1>
          <p className="text-base text-purple-light/70">
            Create your account to open your reports, save access, and continue
          </p>
        </div>

        <div className="flex w-full flex-col gap-1">
          {!isAndroid && (
            <>
              <button
                type="button"
                onClick={handleApple}
                className="flex h-14 w-full items-center justify-center gap-2 rounded-3xl bg-purple-light/10 text-base font-semibold text-white transition-colors hover:bg-purple-light/15"
              >
                <AppleIcon className="size-[22px] fill-white" />
                Sign in with Apple
              </button>

              <div className="flex items-center gap-4 px-4 py-2">
                <span className="h-px flex-1 bg-purple-light/20" />
                <span className="text-base text-purple-light/40">or</span>
                <span className="h-px flex-1 bg-purple-light/20" />
              </div>
            </>
          )}

          <button
            type="button"
            onClick={handleGoogle}
            className="flex h-14 w-full items-center justify-center gap-2 rounded-3xl bg-purple-light/10 text-base font-semibold text-white transition-colors hover:bg-purple-light/15"
          >
            <GoogleIcon className="size-[22px]" />
            Sign in with Google
          </button>
        </div>
      </MobileLayout>
    </div>
  )
}
